import { useCallback, useEffect, useRef } from 'react';
import { marked } from 'marked';
import { waitForConnectionWithToast } from './ws';
import { waitForEvent, waitForEventResults } from './api/events';

export function fmtCost(n: number | undefined | null): string {
  if (n == null) return '—';
  return `$${n < 0.01 ? n.toFixed(4) : n.toFixed(2)}`;
}

export function fmtTokens(n: number | undefined | null): string {
  if (n == null) return '—';
  return n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n);
}

// A failed-workflow notification's `error` carries the handler's raw JSON body
// (`{"error":{"code":...,"message":...}}`) rather than a plain string.
export function parseEelError(raw: string): string {
  const jsonStart = raw.indexOf('{');
  if (jsonStart === -1) return raw;
  try {
    const parsed = JSON.parse(raw.slice(jsonStart)) as { error?: { message?: string } };
    return parsed.error?.message ?? raw;
  } catch { return raw; }
}

export function renderMarkdown(text: string): string {
  return marked.parse(text, { async: false });
}

export function errMessage(e: unknown, fallback: string): string {
  return e instanceof Error ? parseEelError(e.message) : fallback;
}

export const STALE_MESSAGE = 'This record was changed by someone else. Reload to see the latest version.';

export async function runEvent(emitFn: (connectionId: string) => Promise<string>, timeoutMs = 30_000): Promise<void> {
  const connId = await waitForConnectionWithToast();
  if (!connId) throw new Error('Unable to connect to server');
  const eventId = await emitFn(connId);
  await waitForEvent(eventId, timeoutMs);
}

export async function runEventForResults(emitFn: (connectionId: string) => Promise<string>, timeoutMs = 30_000): Promise<Record<string, unknown>> {
  const connId = await waitForConnectionWithToast();
  if (!connId) throw new Error('Unable to connect to server');
  const eventId = await emitFn(connId);
  return waitForEventResults(eventId, timeoutMs);
}

// An event's completion notification can arrive before the relationship edges it creates or removes are readable, so a single
// reload straight after can show the old state indefinitely. Reload now, then again shortly after to pick up the settled state.
const SETTLE_DELAYS_MS = [2_000, 5_000];

export function useSettledReload(reload: () => void): () => void {
  const reloadRef = useRef(reload);
  reloadRef.current = reload;
  const timers = useRef<number[]>([]);
  useEffect(() => () => { timers.current.forEach(clearTimeout); }, []);
  return useCallback(() => {
    reloadRef.current();
    timers.current.forEach(clearTimeout);
    timers.current = SETTLE_DELAYS_MS.map(ms => window.setTimeout(() => reloadRef.current(), ms));
  }, []);
}
